/**
 * WBS elements — SAP backend implementation.
 *
 * Part of the isolated `sap-backend/` side project (see ../README.md). Backs the
 * dataService `getWbsElements(projectId)` contract with the live project
 * structure from SAP PS (PRPS + PROJ + PRHI), exposed by the custom OData V4
 * service ZUI_M12_WBS_STRUCT_O4 (CDS ZC_M12_WBS_STRUCT, package ZMACH12, built
 * in this phase).
 *
 * SCOPE NOTE: this returns WBS only for a project whose external S/4 ID matches
 * the `projectId` passed in. App demo projects (proj_*) have no S/4 counterpart
 * and return []. Real deployed projects (the ones pushed to PROJ/PRPS) return
 * their tree. This is the honest behavior of "system of record = S/4".
 *
 * STRUCTURE ONLY: PRPS gives the hierarchy (id, parent, level, flags) but not
 * budget or EVM schedule metrics — those are derived/forecast data sourced
 * elsewhere (actuals already come from ACDOCA; budget/schedule stay zero here,
 * matching the app's zero-default shape until a costing read is added).
 */

import type { WbsElement } from '@/types/ppm'
import { S4_PROXY_BASE } from '../config'
import { odataGetCollection, odataLiteral } from '../odataClient'

/** Runtime path uses `srvd` (not the published `srvd_a2x`) — same kernel quirk
 *  as every other Mach12 V4 service (see currencyRatesService.ts). */
const WBS_STRUCT_PATH = `${S4_PROXY_BASE}/zui_m12_wbs_struct_o4/srvd/sap/z_m12_wbs_struct_sd/0001/WbsStructure`

/** Row shape from ZC_M12_WBS_STRUCT (see its $metadata). */
interface RawWbsStructRow {
  WbsElement: string
  ProjectID: string
  ParentWbsElement: string
  Description: string
  WbsLevel: number
  CompanyCode: string
  IsBillingElement: boolean
  IsPlanningElement: boolean
  IsAccountAssignment: boolean
  ProjectType: string
  ProjectName: string
}

/** Human label for the WBS element kind, from the PS indicator flags. */
function wbsType(r: RawWbsStructRow): string {
  if (r.IsBillingElement) return 'Billing Element'
  if (r.IsAccountAssignment) return 'Account Assignment'
  if (r.IsPlanningElement) return 'Planning Element'
  return 'WBS Element'
}

/**
 * Read the WBS tree for a project from SAP PS. Returns a flat WbsElement[] with
 * parentWbsId set (matching the app's flat shape from assembleWbs). Without a
 * projectId we return [] — the SAP view holds every project's WBS and must be
 * scoped.
 */
export async function getWbsElements(projectId?: string): Promise<WbsElement[]> {
  if (!projectId) {
    console.warn('[sap-backend] getWbsElements requires a projectId in sap mode; returning []')
    return []
  }

  const filter = `ProjectID eq '${odataLiteral(projectId)}'`
  const url = `${WBS_STRUCT_PATH}?$filter=${encodeURIComponent(filter)}&$top=5000&$orderby=WbsElement`
  const rows = await odataGetCollection<RawWbsStructRow>(url)

  return rows.map((r) => ({
    id: r.WbsElement,
    projectId: r.ProjectID || projectId,
    code: r.WbsElement,
    name: r.Description,
    parentWbsId: r.ParentWbsElement ? r.ParentWbsElement : null,
    level: r.WbsLevel ?? 0,
    type: wbsType(r),
    // Structure read: budget/EVM are not in PRPS. Mirror the app's zero-default
    // shape; actuals already come from ACDOCA via getPeriodicActuals.
    budget: { total: 0, spent: 0, committed: 0, available: 0, currency: 'USD' },
    schedule: { percentComplete: 0, spi: 0, cpi: 0, scheduleVarianceDays: 0 },
  }))
}

/** A flat WBS pick-list item (across projects), for a searchable form lookup. */
export interface WbsListItem {
  wbs: string
  description: string
  projectId: string
  projectName: string
  type: string
}

/**
 * List WBS elements ACROSS projects for a searchable picker. Unlike
 * getWbsElements (which scopes to one project to build a tree), this returns a
 * flat catalog the user searches/filters against. Optionally narrow to one
 * ProjectID. Capped so the payload stays reasonable.
 */
export async function getWbsElementList(opts?: { projectId?: string; top?: number; filters?: string[] }): Promise<WbsListItem[]> {
  const parts = [`$top=${opts?.top ?? 5000}`, '$orderby=WbsElement']
  const clauses: string[] = []
  if (opts?.projectId) clauses.push(`ProjectID eq '${odataLiteral(opts.projectId)}'`)
  if (opts?.filters?.length) clauses.push(...opts.filters)
  if (clauses.length) parts.push(`$filter=${encodeURIComponent(clauses.join(' and '))}`)
  const rows = await odataGetCollection<RawWbsStructRow>(`${WBS_STRUCT_PATH}?${parts.join('&')}`)
  return rows.map((r) => ({
    wbs: r.WbsElement,
    description: r.Description,
    projectId: r.ProjectID,
    projectName: r.ProjectName,
    type: wbsType(r),
  }))
}
